import { mergeOptions } from '../../../libs/gin-tonic/helpers/common/merge-options';

interface TabElement {
	index: number;
	navigation: HTMLElement;
	element: HTMLElement;
}

export interface TabsOptions {
	navigationSelector: string;
	wrapperSelector: string;
	contentSelector: string;
	activeClass: string;
	visibleClass: string;
	contentIdentifierAttribute: string;
	navigationToContentAttribute: string;
}

export class Tabs {
	static options: TabsOptions = {
		navigationSelector: '.js-tab__link',
		wrapperSelector: '.js-tab__wrapper',
		contentSelector: '.js-tab__content',
		activeClass: 'is-active',
		visibleClass: 'is-visible',
		contentIdentifierAttribute: 'data-tab',
		navigationToContentAttribute: 'data-tab-action'
	};

	private wrapperElement: HTMLElement | null = null;
	private tabElements: TabElement[] = [];
	private visibleTabIndex: number | null = null;
	private options: TabsOptions;

	constructor(public element: HTMLElement, options?: Partial<TabsOptions>) {
		this.options = mergeOptions(Tabs, options, element);

		let index = 0;
		this.wrapperElement = this.element.querySelector(this.options.wrapperSelector);

		this.element.querySelectorAll<HTMLElement>(this.options.navigationSelector).forEach(navigation => {
			const name = navigation.getAttribute(this.options.navigationToContentAttribute);
			const element = this.element.querySelector<HTMLElement>(`${this.options.contentSelector}[${this.options.contentIdentifierAttribute}="${name}"]`);

			if (!element) {
				return;
			}

			this.tabElements.push({
				index: index++,
				navigation,
				element
			});
		});

		this.tabElements.forEach(tabElement => {
			tabElement.navigation.addEventListener('click', event => {
				//event.stopPropagation(); important for blazor
				event.preventDefault();
				this.openTab(tabElement);
			});
		});

		const activeTabElement = this.getActiveTab();
		if (activeTabElement) {
			this.openTab(activeTabElement);
		}
	}

	private openTab(tab: TabElement): void {
		if (this.visibleTabIndex === tab.index) {
			return;
		}

		const visibleTab = this.getTabByIndex(this.visibleTabIndex);
		visibleTab?.navigation.classList.remove(this.options.activeClass);
		visibleTab?.element.addEventListener(
			'transitionrun',
			() => {
				tab.navigation.classList.add(this.options.activeClass);
				tab.element.classList.add(this.options.visibleClass);
			},
			{ once: true }
		);
		visibleTab?.element.classList.remove(this.options.visibleClass);
		this.visibleTabIndex = tab.index;

		if (this.wrapperElement) {
			this.wrapperElement.style.height = (tab.element.getBoundingClientRect().height || 0) + 'px';
		}
	}

	private getActiveTab() {
		return this.tabElements.find(tabElement => tabElement.navigation.classList.contains(this.options.activeClass));
	}

	private getTabByIndex(index: number | null) {
		return this.tabElements.find(tabElement => tabElement.index === index);
	}
}
